/**
 * causticsPipeline — refracted light through water.
 *
 * Caustics are the bright/dark patterns formed when light passes
 * through a wavy water surface. We approximate them by computing
 * the squared Laplacian of a noise field — the integral of
 * brightness over an area. This produces the characteristic sharp
 * curved lines of underwater caustics.
 *
 * The water surface itself is animated via vertical sine waves on a
 * domain-warped noise field. Audio drives the wave amplitude.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CausticsPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Multi-layer water surface distortion.
  vec2 distorted = p;
  distorted += vec2(
    sin(p.y * 6.0 + uTime * 0.8) * 0.10,
    cos(p.x * 5.0 + uTime * 0.6) * 0.08
  );
  distorted += vec2(
    sin(p.y * 11.0 + uTime * 1.5) * 0.04,
    cos(p.x * 13.0 + uTime * 1.2) * 0.04
  );

  // Caustics: ridged-multifractal noise approximation.
  // Sample field at p and nearby; caustic intensity = 1/(|field|) modulated.
  vec2 dp = vec2(0.004, 0.0);
  float n1 = fbm(distorted + dp.xy);
  float n2 = fbm(distorted - dp.xy);
  float n3 = fbm(distorted + dp.yx);
  float n4 = fbm(distorted - dp.yx);
  float gx = n1 - n2;
  float gy = n3 - n4;
  float gmag2 = gx * gx + gy * gy;
  // Caustic brightness: high when |grad| is at the right level (magic band).
  // Use a peaked function around an optimal gmag.
  float caustic = exp(-pow((gmag2 - 0.002) * 600.0, 2.0));
  // Add a high-frequency ridge layer for sparkle.
  vec2 sparkUv = distorted * 12.0 + uTime * 0.5;
  float spark = sin(sparkUv.x) * sin(sparkUv.y);
  spark = smoothstep(0.7, 0.95, abs(spark));
  // Audio modulation.
  float bassMul = 0.5 + bass * 1.5;
  float highMul = 0.5 + high * 2.0;
  caustic *= bassMul * highMul;
  spark *= highMul;

  // Background: deep ocean blue with a skyward gradient.
  vec3 deepWater = vec3(0.01, 0.04, 0.10);
  vec3 skyWater = vec3(0.04, 0.16, 0.30);
  vec3 col = mix(deepWater, skyWater, smoothstep(-1.0, 1.0, p.y));
  // Caustic highlights: hot white-blue.
  vec3 causticColor = vec3(0.85, 1.0, 1.0);
  col += causticColor * caustic * 1.5;
  col += causticColor * spark * 2.0;
  // Subtle wave ripples — distance to nearest multiple of 6.28.
  float ripple = sin(p.y * 18.0 + uTime) * cos(p.x * 16.0 - uTime * 0.5);
  col += vec3(0.10, 0.30, 0.45) * smoothstep(0.85, 1.0, ripple) * 0.5;

  // Mid-audio: pulse the entire scene brightness.
  col *= 1.0 + mid * 0.4;

  // Vignette.
  float r = length(p);
  float vig = 1.0 - r * r * 0.4;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeCausticsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeCausticsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createCausticsPipeline(gl: WebGL2RenderingContext): CausticsPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawCaustics(
  gl: WebGL2RenderingContext,
  pipe: CausticsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
